<template>
  <div class="t-bg-white t-border t-border-gray-300 t-shadow-md t-pt-2 t-pb-4 t-px-5 t-overflow-hidden md:t-rounded-lg" role="contentinfo">
    <!-- Card Heading -->
    <div class="t-flex t-flex-col t-items-baseline t-py-2 md:t-flex-row md:t-justify-between">
      <p class="t-flex t-flex-wrap t-text-2xl t-font-bold">
        {{ location.name }}
      </p>
      <div
        v-if="location.ownershipStatus === 'OWNED_BY_YOU'"
        class="t-bg-gray-700 t-rounded t-px-3 t-py-1 t-text-sm t-font-medium t-uppercase t-tracking-wide"
        style="color: #2F533F; background-color: #D0F5D7"
      >
        Owned By You
      </div>
      <div
        v-else-if="location.ownershipStatus === 'OWNED_BY_ANOTHER'"
        class="t-bg-gray-700 t-rounded t-px-3 t-py-1 t-text-sm t-font-medium t-uppercase t-tracking-wide"
        style="color: #318b89; background-color: #c5e7e8"
      >
        Owned By Another
      </div>
    </div>

    <!-- Location Features -->
    <div class="t-border-t-2 t-border-b-2 t-border-gray-400 t-flex t-flex-col md:t-flex-row md:t-justify-between">
      <div class="t-flex t-flex-wrap t-py-3 md:t-flex-row">
        <div
          :class="{'t-line-through': !location.pickup}"
          class="t-text-lg t-font-light md:t-border-r-2 md:t-border-gray-400 t-px-4 md:t-pl-0"
        >
          Pickups
        </div>
        <div
          :class="{'t-line-through': !location.delivery}"
          class="t-text-lg t-font-light md:t-border-r-2 md:t-border-gray-400 t-px-4"
        >
          Deliveries
        </div>
        <div
          :class="{'t-line-through': !location.has_forklift}"
          class="t-text-lg t-font-light md:t-border-r-2 md:t-border-gray-400 t-px-4">
            Forklift
          </div>
        <div class="t-text-lg t-font-light md:t-border-r-2 md:t-border-gray-400 t-px-4">
          <span
            v-tooltip="'Your warehouse can earn you cash by allowing it to be used as a Freighthub depot. ' +
            'You are paid $3 per pallet lift (unloading or loading), $1 per carton lift (unloading or loading), ' +
            '$0.50 per pallet per day storage fee if more than 24 hours, $50 per hour to extend hours beyond normal operating hours. ' +
            'Call Alan on 0274 759 750 to discuss further.'"
          >
            <span
              :class="{'t-line-through': !location.depot}"
              class="t-text-lg t-font-light">
              Depot
            </span>
            <i class="fa fa-question-circle d-inline" aria-hidden="true" style="margin-left: 0.25rem; white-space: pre-line"/>
          </span>
        </div>
        <div
          v-if="location.wait_time"
          class="t-self-center text-muted t-px-4"
          v-tooltip:bottom="'The average time a truck spends at this location when loading and unloading.'"
        >
          <div :style="{ background: getTruckWaitingTimeColor(location.wait_time) }">
            <i class="far fa-clock"></i>
            {{ location.wait_time }}m
          </div>
        </div>
      </div>

      <div class="t-hidden md:t-flex t-items-center t-font-semibold">
        <button v-if="location.defaults.pickup" class="t-bg-gray-700 t-rounded t-mr-1 t-px-3 t-py-1 t-cursor-default">
          <span class="t-text-white t-text-xs t-font-semibold t-uppercase t-tracking-widest">
            Default Pickup Location
          </span>
        </button>

        <button
          @click="defaultPickupChanged(location)"
          v-if="!location.defaults.pickup && location.pickup"
          class="t-bg-gray-500 t-rounded t-mr-1 t-px-3 t-py-1"
        >
          <span
            class="t-text-white t-text-xs t-font-semibold t-uppercase t-tracking-widest"
          >
            Make Default Pickup
          </span>
        </button>

        <button @click="$emit('editLocation', location)" class="t-px-2 t-text-blue-700 focus:t-outline-none">
          Edit
        </button>

        <button @click="$emit('deleteLocation', location)" class="t-px-2 t-text-red-700 focus:t-outline-none">
          Delete
        </button>
      </div>
    </div>

    <!-- Location Information -->
    <div class="t-flex t-flex-col t-mt-2 t-space-y-3 md:t-flex-row md:t-space-y-0">
      <div class="t-w-full md:t-w-1/3">
        <p class="t-text-sm t-tracking-wide t-text-gray-800 t-font-semibold">
          LOCATION
        </p>
        <p>
          {{ location.address.data.prefix || '' }} {{ `${location.address.data.street_number} ${location.address.data.street_name}` }}
        </p>
        <p>
          {{ location.address.data.suburb }}
        </p>
        <p>
          {{ location.address.data.city }}
        </p>
      </div>

      <div class="t-w-full md:t-w-1/3">
        <p class="t-text-sm t-tracking-wide t-text-gray-800 t-font-semibold">
          CONTACT
        </p>
        <p v-if="location.contact_name">{{ location.contact_name }}</p>
        <div>
          <p v-if="location.email">{{ location.email }}</p>
          <div
            v-else
            v-tooltip:top="'Contact email unavailable. Adding an email helps prevent delivery delays or slightly higher charges to this location'"
            class="t-inline-flex t-items-center"
          >
            <i class="fas fa-exclamation-triangle"></i>
            <span class="t-text-gray-800 t-ml-2">No email provided</span>
          </div>
        </div>
        <p v-if="location.phone">{{ location.phone }}</p>
      </div>

      <div class="t-w-full md:t-w-1/3">
        <p class="t-text-sm t-tracking-wide t-text-gray-800 t-font-semibold">
          INSTRUCTIONS
        </p>
        <p class="t-flex t-flex-wrap">
          {{ location.instructions || '' }}
        </p>
      </div>
    </div>

    <!-- Default pickup option, Edit and Delete Icons for smaller screens -->
    <div class="t-flex t-flex-col t-mt-8 md:t-hidden">
      <!-- Default Pickup Option -->
      <button v-if="location.defaults.pickup" class="t-bg-gray-700 t-rounded t-mb-3 t-px-3 t-py-1 t-cursor-default focus:t-outline-none">
          <span class="t-text-white t-text-xs t-font-semibold t-uppercase t-tracking-widest">
            Default Pickup Location
          </span>
      </button>

      <button
        @click="defaultPickupChanged(location)"
        v-if="!location.defaults.pickup && location.pickup"
        class="t-bg-gray-500 t-rounded t-mb-3 t-px-3 t-py-1"
      >
          <span
            class="t-text-white t-text-xs t-font-semibold t-uppercase t-tracking-widest"
          >
            Make Default Pickup
          </span>
      </button>

      <!-- Edit -->
      <button
        @click.prevent="$emit('editLocation', location)"
        class="t-flex t-inline-flex t-justify-center t-bg-dodger-blue hover:t-bg-blue-500 t-text-white t-font-medium t-rounded-md t-px-4 t-py-2 t-mb-3"
      >
        Edit
      </button>

      <!-- Delete -->
      <button
        @click.prevent="$emit('deleteLocation', location)"
        class="t-flex t-inline-flex t-justify-center t-bg-red-600 hover:t-bg-red-700 t-text-white t-font-medium t-rounded-md t-px-4 t-py-2 t-mb-3"
      >
        Delete
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LocationCard',
  description: 'A card containing location information',
  emits: ['onDeleteLocation', 'onDefaultPickupChanged'],
  props: ['location'],
  methods: {
    defaultPickupChanged(location) {
      axios
        .patch(`/settings/locations/default-pickup/${location.id}`, {}, { headers: { Accept: 'application/json' } })
        .then(() => {
          this.$emit('defaultChanged', location);
          this.$swal({
            title: 'Default pickup location updated!',
            type: 'success',
            confirmButtonText: 'Okay',
          });
        })
        .catch(() => Event.$emit('serverError'));
    },
    getTruckWaitingTimeColor(waitingTime) {
      let colour;
      let waitTime = Number.parseInt(waitingTime);
      if (waitTime > 20) {
        colour = "#ff8aa3";
      } else if (waitTime < 10) {
        colour = "#5cff7c";
      } else {
        colour = "#ffe978";
      }
      return colour;
    }
  }
}
</script>

<style scoped>

</style>
